import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type CoverageFlatTeamSettingGroupId =
  | "coverage.text_localization"
  | "coverage.info_modal"
  | "coverage.general_coverage"
  | "coverage.exclusions"
  | "coverage.return_coverage"
  | "coverage.credit_incentive"
  | "coverage.checkout_ui_customizations"
  | "coverage.package_protection"
  | "coverage.pre_protected_orders"
  | "coverage.advanced_coverage"
  | "coverage.digital_warehouse"
  | "coverage.redo_item_fulfillment";

const infoModalOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.infoModal",
  product: "returns",
};

const settingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings",
  product: "returns",
};

const shopifyExtensionOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.shopifyExtension",
  product: "returns",
};

const checkoutUICustomizationsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.shopifyExtension.checkoutUICustomizations",
  product: "returns",
};

const creditIncentiveOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.shopifyExtension.coverageCreditIncentive",
  product: "returns",
};

const packageProtectionOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.packageProtection",
  product: "returns",
};

const customerFacingRisk: SettingRisk = {
  level: "medium",
  reason:
    "Changes affect the customer-facing return coverage widget and modal shoppers see at checkout.",
};

const digitalWarehouseOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.shopifyExtension.digitalWarehouse",
  product: "returns",
};

const redoItemFulfillmentOwner: SettingOwner = {
  kind: "teamSettings",
  path: "automation.fulfill_redo_item",
  product: "returns",
};

const coverageEligibilityRisk: SettingRisk = {
  level: "high",
  reason:
    "Changes affect which orders receive return coverage and what shoppers are charged for it.",
};

const coverageParentPageTarget = {
  type: "settings",
  path: "coverage",
} as const;

const coverageBuilderPageTarget = {
  type: "settings",
  path: "coverage/coverage-builder",
} as const;

export const CoverageFlatTeamSettingGroups = [
  {
    id: "coverage.text_localization",
    title: "Text localization",
    description:
      "Default translations and merchant text overrides for storefront Coverage widgets.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: shopifyExtensionOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageBuilderPageTarget,
    synonyms: ["default coverage translations", "coverage language defaults"],
  },
  {
    id: "coverage.info_modal",
    title: "Coverage info modal",
    description:
      "The 'learn more' modal shoppers open from the return coverage toggle: header and body copy, imagery, cart/checkout entry points, and custom styling.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: infoModalOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "coverage/info-modal" },
    synonyms: [
      "info modal",
      "coverage modal",
      "learn more modal",
      "info popup",
      "return coverage modal",
      "modal customization",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing the customer-facing coverage info modal copy or imagery.",
    },
  },
  {
    id: "coverage.general_coverage",
    title: "Coverage availability",
    description:
      "Where return coverage is offered to shoppers: showing the coverage toggle on the product detail page, its info text, font size and CSS selector, and excluding in-store pickup orders from coverage.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: settingsOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "coverage availability",
      "coverage eligibility",
      "show on product page",
      "product page coverage",
      "exclude in-store pickup",
      "coverage info text",
      "coverage font size",
      "coverage css selector",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing where return coverage is offered to shoppers or how it appears on the product page.",
    },
  },
  {
    id: "coverage.exclusions",
    title: "Coverage exclusions",
    description:
      "Rules that exclude orders, sources, customers, properties, metafields, and discount codes from being offered return coverage.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: settingsOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "coverage exclusions",
      "exclude order tags",
      "exclude sources",
      "exclude customer tags",
      "exclude properties",
      "exclude variant metafields",
      "exclude discount codes",
      "require exact match",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing which orders and products are excluded from coverage.",
    },
  },
  {
    id: "coverage.return_coverage",
    title: "Return coverage",
    description:
      "The return coverage offering: which return methods are covered, final-sale coverage, and per-item exclusions such as product tags, collections, and SKUs.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: settingsOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "return coverage",
      "covered return methods",
      "final sale coverage",
      "exclude product tags",
      "exclude collections",
      "exclude skus",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing which return methods are covered or which items are excluded.",
    },
  },
  {
    id: "coverage.credit_incentive",
    title: "Coverage credit incentive",
    description:
      "Store credit given to shoppers who add coverage to an order: whether it is on, a fixed or percentage amount, the minimum purchase, when the credit expires, and the credit and expiration reminder emails.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: creditIncentiveOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "coverage credit incentive",
      "coverage store credit incentive",
      "store credit for coverage",
      "credit for buying coverage",
      "coverage ltv driver",
    ],
    changeGuidance: {
      summary:
        "Confirm before turning the incentive on or changing the credit amount, because it gives customers store credit.",
    },
  },
  {
    id: "coverage.checkout_ui_customizations",
    title: "Checkout UI customizations",
    description:
      "Appearance of the Redo coverage element in Shopify checkout: title and subtext sizes and the spacing around the element.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: checkoutUICustomizationsOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageBuilderPageTarget,
    synonyms: [
      "checkout ui customizations",
      "title text size",
      "subtext size",
      "checkout spacing",
      "checkout coverage appearance",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing the coverage element's checkout appearance.",
    },
  },
  {
    id: "coverage.package_protection",
    title: "Package Protection pricing",
    description:
      "What shoppers pay for package protection at checkout: the pricing strategy, the price floor and ceiling, the cart totals it is offered between, and the domestic and international pricing tiers.",
    productArea: "returns",
    access: {
      read: {
        minVisibility: "merchant",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      write: {
        mode: "none",
        reason:
          "Admin-only under Package Protection Plus, and the bounds only run on the coverage page's save. The consolidated tab reports these values without editing them.",
      },
    },
    owner: packageProtectionOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "never" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "package protection pricing",
      "package protection price",
      "shipping protection price",
      "protection pricing tiers",
      "package protection min price",
      "package protection max price",
    ],
    changeGuidance: {
      summary:
        "Changing these changes what every shopper is charged for package protection — confirm the tiers before saving.",
    },
  },
  {
    id: "coverage.pre_protected_orders",
    title: "Pre-Protected orders",
    description:
      "Automatically add return coverage to eligible orders that were placed without it.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: settingsOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "pre-protected orders",
      "pre protected orders",
      "retroactive coverage",
      "auto add coverage",
    ],
    changeGuidance: {
      summary:
        "Confirm before enabling automatic coverage on orders placed without it.",
    },
  },
  {
    id: "coverage.advanced_coverage",
    title: "Advanced coverage",
    description:
      "Advanced Shopify coverage behavior, including whether Redo removes the cart properties it adds during checkout after each order is created.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: shopifyExtensionOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "advanced coverage",
      "delete cart properties",
      "cart properties",
      "shopify coverage advanced",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing advanced Shopify coverage cart behavior.",
    },
  },
  {
    id: "coverage.digital_warehouse",
    title: "Redo digital warehouse",
    description:
      "Keeps the Redo coverage product in stock through its own Redo digital warehouse location in Shopify, so it never uses up inventory at your locations.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: digitalWarehouseOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: ["digital warehouse", "redo warehouse"],
    changeGuidance: {
      summary:
        "Confirm before turning this on or off, since it changes where Shopify tracks stock for the Redo coverage product.",
    },
  },
  {
    id: "coverage.redo_item_fulfillment",
    title: "Redo item fulfillment",
    description:
      "When Shopify marks the Redo coverage line item as fulfilled on an order: never on its own, once you fulfill another item, or as soon as the order is created.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: redoItemFulfillmentOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "redo item fulfillment",
      "fulfill redo item",
      "partial redo item fulfillment",
      "immediate fulfillment",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing when the Redo item is fulfilled, since it changes the fulfillment status of new orders.",
    },
  },
] as const satisfies readonly SettingGroupDefinition<CoverageFlatTeamSettingGroupId>[];

type _assertAllCoverageFlatTeamSettingGroupIdsProvided =
  CoverageFlatTeamSettingGroupId extends (typeof CoverageFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allCoverageFlatTeamSettingGroupIdsProvided: _assertAllCoverageFlatTeamSettingGroupIdsProvided = true;
void _allCoverageFlatTeamSettingGroupIdsProvided;

export const CoverageFlatTeamSettingGroupById: Record<
  CoverageFlatTeamSettingGroupId,
  SettingGroupDefinition<CoverageFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      CoverageFlatTeamSettingGroupId,
      SettingGroupDefinition<CoverageFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of CoverageFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    CoverageFlatTeamSettingGroupId,
    SettingGroupDefinition<CoverageFlatTeamSettingGroupId>
  >;
})();
